import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Email,
  Refresh,
  Visibility,
  FilterList,
  Clear,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface EmailLog {
  id: number;
  to: string;
  cc: string | null;
  bcc: string | null;
  from: string;
  from_name: string | null;
  subject: string;
  body: string | null;
  html_body: string | null;
  mail_class: string | null;
  template: string | null;
  metadata: any;
  status: 'sent' | 'failed';
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
}

interface EmailLogStats {
  total: number;
  sent: number;
  failed: number;
  today: number;
  this_week: number;
  this_month: number;
}

const EmailLogs: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [toFilter, setToFilter] = useState<string>('');
  const [subjectFilter, setSubjectFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  // Check if user is admin
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  useEffect(() => {
    if (isPmpAdmin()) {
      fetchEmailLogs();
      fetchStats();
    }
  }, [page, rowsPerPage, statusFilter, toFilter, subjectFilter, dateFromFilter, dateToFilter]);

  const fetchEmailLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        per_page: rowsPerPage,
      };

      if (statusFilter) params.status = statusFilter;
      if (toFilter) params.to = toFilter;
      if (subjectFilter) params.subject = subjectFilter;
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;

      const response = await api.get('/email-logs', { params });
      setLogs(response.data.data.data);
      setTotalCount(response.data.data.total);
    } catch (error: any) {
      console.error('Error fetching email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/email-logs/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  };

  const handleViewLog = (log: EmailLog) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setToFilter('');
    setSubjectFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (!isPmpAdmin()) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">You do not have permission to view email logs.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 3,
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Email color="primary" />
            <Typography variant="h4">Email Logs</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchEmailLogs();
              fetchStats();
            }}
            disabled={loading}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Refresh
          </Button>
        </Box>

        {/* Statistics Cards */}
        {stats && (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: { xs: 1.5, sm: 2 }, 
            mb: 3 
          }}>
            <Box sx={{ 
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 calc(33.333% - 16px)', md: '1 1 calc(16.666% - 16px)' }, 
              minWidth: { xs: 'calc(50% - 8px)', sm: 150 } 
            }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Emails
                  </Typography>
                  <Typography variant="h5">{stats.total}</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Sent
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {stats.sent}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Failed
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {stats.failed}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Today
                  </Typography>
                  <Typography variant="h5">{stats.today}</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    This Week
                  </Typography>
                  <Typography variant="h5">{stats.this_week}</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    This Month
                  </Typography>
                  <Typography variant="h5">{stats.this_month}</Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* Filters */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: 2, 
            mb: 2 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <FilterList />
              <Typography variant="h6">Filters</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={handleClearFilters}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Clear
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200 }}>
              <TextField
                fullWidth
                size="small"
                label="To (Email)"
                value={toFilter}
                onChange={(e) => setToFilter(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200 }}>
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(16.666% - 16px)', minWidth: 150 }}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Email Logs Table */}
        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>To</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>From</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Subject</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Mail Class</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Sent At</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">No email logs found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Chip
                        icon={log.status === 'sent' ? <CheckCircle /> : <ErrorIcon />}
                        label={log.status}
                        color={log.status === 'sent' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{log.to}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {log.from_name ? `${log.from_name} <${log.from}>` : log.from}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={log.subject}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: { xs: 150, sm: 300 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          {log.subject}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {log.mail_class ? (
                        <Tooltip title={log.mail_class}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {log.mail_class.split('\\').pop()}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {log.template || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {log.sent_at
                        ? new Date(log.sent_at).toLocaleString()
                        : new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewLog(log)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </TableContainer>

        {/* Email Detail Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Email Details</Typography>
              <Chip
                icon={selectedLog?.status === 'sent' ? <CheckCircle /> : <ErrorIcon />}
                label={selectedLog?.status}
                color={selectedLog?.status === 'sent' ? 'success' : 'error'}
                size="small"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      To
                    </Typography>
                    <Typography variant="body1">{selectedLog.to}</Typography>
                  </Box>
                  {selectedLog.cc && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        CC
                      </Typography>
                      <Typography variant="body1">{selectedLog.cc}</Typography>
                    </Box>
                  )}
                  {selectedLog.bcc && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        BCC
                      </Typography>
                      <Typography variant="body1">{selectedLog.bcc}</Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      From
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.from_name
                        ? `${selectedLog.from_name} <${selectedLog.from}>`
                        : selectedLog.from}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Subject
                  </Typography>
                  <Typography variant="body1">{selectedLog.subject}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {selectedLog.mail_class && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Mail Class
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {selectedLog.mail_class}
                      </Typography>
                    </Box>
                  )}
                  {selectedLog.template && (
                    <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Template
                      </Typography>
                      <Typography variant="body2">{selectedLog.template}</Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 250 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Sent At
                    </Typography>
                    <Typography variant="body2">
                      {selectedLog.sent_at
                        ? new Date(selectedLog.sent_at).toLocaleString()
                        : new Date(selectedLog.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                {selectedLog.error_message && (
                  <Box>
                    <Alert severity="error">
                      <Typography variant="subtitle2">Error Message</Typography>
                      <Typography variant="body2">{selectedLog.error_message}</Typography>
                    </Alert>
                  </Box>
                )}
                {selectedLog.html_body && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      HTML Body
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxHeight: 400,
                        overflow: 'auto',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: selectedLog.html_body }}
                        style={{ fontSize: '12px' }}
                      />
                    </Paper>
                  </Box>
                )}
                {selectedLog.body && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Text Body
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxHeight: 400,
                        overflow: 'auto',
                        bgcolor: 'grey.50',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                      }}
                    >
                      {selectedLog.body}
                    </Paper>
                  </Box>
                )}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Metadata
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxHeight: 300,
                        overflow: 'auto',
                        bgcolor: 'grey.50',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                      }}
                    >
                      <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EmailLogs;

